import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import MathText from "@/components/Math";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, RefreshCw, ArrowLeft, Timer, Sparkles } from "lucide-react";

function normalise(s) {
  if (s == null) return "";
  return String(s).replace(/\$/g, "").replace(/\\\\/g, "").replace(/[.\s]/g, "").toLowerCase();
}

export default function FullMock() {
  const { classId } = useParams();
  const { user } = useAuth();
  const [mock, setMock] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setSubmitted(false); setAnswers({}); setSeconds(0); setSaved(false);
    const { data } = await api.get(`/full-mock/${classId}`);
    setMock(data);
    setRunning(true);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classId]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const submit = async () => {
    setSubmitted(true); setRunning(false);
    if (!user || saved || !mock) return;
    // Score by marks: correct final match awards full marks for that question.
    let scored = 0, totalMarks = mock.total_marks;
    for (const sec of mock.sections) {
      for (const q of sec.questions) {
        if (normalise(answers[q.id]) && normalise(answers[q.id]) === normalise(q.final)) {
          scored += q.marks;
        }
      }
    }
    try {
      await api.post("/practice-attempts", {
        chapter_id: `MOCK-${classId}`,
        score: scored,
        total: totalMarks,
        time_seconds: seconds,
      });
      setSaved(true);
    } catch (e) { /* silent */ }
  };

  if (!mock) return <div className="text-zinc-400">Building your mock paper…</div>;

  let scored = 0;
  for (const sec of mock.sections) for (const q of sec.questions) {
    if (normalise(answers[q.id]) && normalise(answers[q.id]) === normalise(q.final)) scored += q.marks;
  }
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="max-w-4xl mx-auto fade-up">
      <Link to={`/sample-papers/${classId}`} className="text-xs text-zinc-400 hover:text-orange-300 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to sample papers
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-1">Full Mock · {mock.class_label} · Attempt {mock.attempt_no}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight gradient-sunset-text" data-testid="full-mock-title">Class-wide Mock Paper</h1>
          <div className="text-sm text-zinc-400 mt-1">{mock.total_questions} questions · {mock.total_marks} marks · {mock.duration_minutes} minutes</div>
        </div>
        <div className="text-right shrink-0">
          <div className="inline-flex items-center gap-2 rounded-full bg-yellow-300/10 border border-yellow-300/20 px-3 py-1 font-mono text-yellow-300 text-sm">
            <Timer className="w-3.5 h-3.5" /> {mm}:{ss}
          </div>
        </div>
      </div>

      <div className="space-y-6 pb-24">
        {mock.sections.map((sec) => (
          <section key={sec.name} className="rounded-lg border border-white/10 bg-[#152040] p-5" data-testid={`section-${sec.name}`}>
            <div className="flex items-baseline justify-between border-b border-white/10 pb-3 mb-4">
              <div>
                <div className="font-display text-lg font-bold text-zinc-100">{sec.name}</div>
                <div className="text-[11px] text-zinc-500">{sec.hint}</div>
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{sec.questions.length} × {sec.marks_per_q} mk</div>
            </div>
            <div className="space-y-4">
              {sec.questions.map((q, i) => {
                const student = answers[q.id] || "";
                const isRight = submitted && normalise(student) && normalise(student) === normalise(q.final);
                const isWrong = submitted && normalise(student) && normalise(student) !== normalise(q.final);
                return (
                  <div key={q.id} className="border-l-2 border-white/10 pl-3">
                    <div className="flex items-start gap-3">
                      <span className="font-mono text-yellow-300 text-xs mt-1">{sec.name.split(" ")[1]}{i + 1}</span>
                      <div className="flex-1">
                        <div className="text-sm text-zinc-100 leading-relaxed"><MathText>{q.q}</MathText></div>
                        <div className="text-[10px] text-zinc-500 mt-1">From: {q.chapter_title} · {q.marks} mark{q.marks > 1 ? "s" : ""}</div>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={student}
                            disabled={submitted}
                            onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                            placeholder="Your final answer"
                            data-testid={`mock-input-${q.id}`}
                            className="flex-1 min-w-[200px] rounded-md bg-[#0b1428] border border-white/10 px-3 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-orange-300/50"
                          />
                          {isRight && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                          {isWrong && <XCircle className="w-4 h-4 text-rose-400" />}
                        </div>
                        {submitted && (
                          <div className="mt-2 text-xs">
                            <div className="text-orange-300"><span className="text-zinc-500">Answer: </span><MathText>{q.final}</MathText></div>
                            <details className="mt-1 text-[11px] text-zinc-400">
                              <summary className="cursor-pointer hover:text-yellow-300">Detailed solution</summary>
                              <div className="mt-1 rounded border border-white/10 bg-[#0b1428] p-2 font-mono space-y-0.5">
                                {q.solution.map((s, j) => (
                                  <div key={j} className="flex gap-2">
                                    <span className="text-yellow-300">{j + 1}.</span>
                                    <MathText>{s}</MathText>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="sticky bottom-4 mt-6">
        {!submitted ? (
          <Button onClick={submit} className="w-full h-12 bg-sky-400 hover:bg-sky-300 text-slate-900 font-semibold rounded-full" data-testid="submit-mock">
            Submit paper
          </Button>
        ) : (
          <div className="rounded-xl border border-orange-300/40 bg-gradient-to-br from-pink-300/10 to-transparent p-5 text-center">
            <div className="font-display text-2xl font-bold gradient-sunset-text">
              You scored {scored} / {mock.total_marks}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              {Math.round(scored / mock.total_marks * 100)}% — {scored >= mock.total_marks * 0.75 ? "Excellent — board-ready!" : scored >= mock.total_marks * 0.5 ? "Solid — revise the ones you missed." : "Time to revisit theory & try again."}
            </div>
            {user && saved && <div className="text-xs text-emerald-400 mt-2">✓ Attempt saved</div>}
            {!user && <div className="text-xs text-zinc-500 mt-2">Sign in to save your score</div>}
            <div className="flex gap-2 justify-center mt-4">
              <Button onClick={load} className="rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="retry-mock">
                <RefreshCw className="w-4 h-4 mr-1" /> New paper
              </Button>
              <Link to="/tutor">
                <Button variant="outline" className="rounded-full border-orange-300/40 text-orange-300 hover:bg-orange-300/10">
                  <Sparkles className="w-4 h-4 mr-1" /> Ask AI Tutor
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
